/**
 * How long each attack family's travel and outcome take on the board (`attack-animations`), in one
 * renderer-free table so the Pixi animator paces its beats with it and the Director's backpressure
 * estimate counts the same numbers. The values are the picked looks' timings from the 0.2
 * prototype (`projects/attack-animations/prototype.html`).
 */
import type { AttackFamily, AttackFx } from "@lancer-vtt/domain";

/** The family an attack with no recorded fx plays: today's dot, sharpened. */
export const DEFAULT_ATTACK_FX: AttackFx = { family: "tracer", palette: "kinetic" };

/** A Smart weapon's lock-on reticle closes on the target before the shot leaves. */
export const SMART_RETICLE_MS = 170;

/** Missiles leave one after another, this far apart, one per caught target. */
export const MISSILE_STAGGER_MS = 70;

/** A Blast's detonation, played where the shot lands, before the dice. */
export const DETONATION_MS = 380;

/** One target's outcome burst. A caught target in an area paces shorter: its float follows at once. */
export const OUTCOME_MS = 200;
export const AREA_OUTCOME_MS = 90;

/** Reduced motion: every travel is one short, still flash. */
export const REDUCED_TRAVEL_MS = 120;

const TRAVEL_MS: Record<AttackFamily, number> = {
  strike: 180,
  tracer: 170,
  volley: 220,
  heavy: 270,
  lobbed: 450,
  missile: 390,
  spray: 300,
  beam: 650,
  swarm: 580,
  glyph: 440,
};

/** How long `fx`'s travel paces, for `targets` caught targets (a missile flies one each). */
export function travelMs(fx: AttackFx, targets: number): number {
  const reticle = fx.smart === true ? SMART_RETICLE_MS : 0;
  if (fx.family === "missile") return reticle + TRAVEL_MS.missile + Math.max(0, targets - 1) * MISSILE_STAGGER_MS;
  // The Cone's cells flash outward one column at a time.
  if (fx.family === "spray" && fx.area?.kind === "cone") return reticle + fx.area.size * 45 + 160;
  if (fx.family === "strike" && fx.thrown === true) return reticle + 240;
  return reticle + TRAVEL_MS[fx.family];
}
